import { Button, Menu, Select, SegmentedControl } from '@mantine/core';
import { IconChevronDown, IconLanguage } from '@tabler/icons-react';
import { forwardRef } from 'react';
import { SUPPORTED_LANGUAGES, usePreferencesStore } from '../../../stores';

export type LanguageSwitcherVariant = 'select' | 'menu' | 'segmented';

export interface LanguageSwitcherProps {
  variant?: LanguageSwitcherVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showFlag?: boolean;
}

const LanguageSwitcher = forwardRef<HTMLDivElement, LanguageSwitcherProps>(
  ({ variant = 'select', size = 'sm', showFlag = true }, ref) => {
    const language = usePreferencesStore((state) => state.language);
    const setLanguage = usePreferencesStore((state) => state.setLanguage);

    const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === language);

    const options = SUPPORTED_LANGUAGES.map((lang) => ({
      value: lang.code,
      label: showFlag ? `${lang.flag} ${lang.label}` : lang.label,
    }));

    if (variant === 'select') {
      return (
        <Select
          ref={ref as any}
          value={language}
          onChange={(value) => value && setLanguage(value)}
          data={options}
          size={size}
          leftSection={<IconLanguage size={16} />}
          searchable
        />
      );
    }

    if (variant === 'menu') {
      return (
        <Menu shadow="md" width={180}>
          <Menu.Target>
            <Button
              variant="subtle"
              size={size}
              rightSection={<IconChevronDown size={14} />}
            >
              {showFlag && currentLang?.flag} {currentLang?.label}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <Menu.Item
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                style={{
                  fontWeight: lang.code === language ? 600 : 400,
                  backgroundColor:
                    lang.code === language
                      ? 'var(--mantine-color-blue-light)'
                      : undefined,
                }}
              >
                {showFlag ? `${lang.flag} ${lang.label}` : lang.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      );
    }

    const shortOptions = SUPPORTED_LANGUAGES.slice(0, 4).map((lang) => ({
      value: lang.code,
      label: showFlag ? lang.flag : lang.label.slice(0, 2).toUpperCase(),
    }));

    return (
      <SegmentedControl
        ref={ref as any}
        value={language}
        onChange={(value) => setLanguage(value)}
        data={shortOptions}
        size={size}
      />
    );
  }
);

LanguageSwitcher.displayName = 'LanguageSwitcher';

export default LanguageSwitcher;
